import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { authKeys } from '@/features/auth/auth-queries';
import {
  authSuccessSchema,
  type AuthSuccess,
  phoneStartResponseSchema,
  phoneVerifyResponseSchema,
  type LoginInput,
  type PhoneRegisterInput,
  type PhoneStartInput,
  type PhoneStartResponse,
  type PhoneVerifyResponse,
  type RegisterInput,
} from '@/features/auth/auth-schemas';
import { api } from '@/lib/api/api-client';
import { useSessionStore } from '@/stores/session-store';

/**
 * Every auth response carries the access token only. The refresh token is set by
 * the API as an HttpOnly + Secure + SameSite=Strict cookie and is never handled here.
 */
function useAuthSuccessHandler(): (response: AuthSuccess) => void {
  const setAccessToken = useSessionStore((state) => state.setAccessToken);
  const queryClient = useQueryClient();

  return (response) => {
    setAccessToken(response.accessToken, Date.now() + response.expiresInSeconds * 1000);
    queryClient.setQueryData(authKeys.session, response.user);
  };
}

export function useLogin(): UseMutationResult<AuthSuccess, Error, LoginInput> {
  const onAuthSuccess = useAuthSuccessHandler();

  return useMutation({
    mutationFn: async (input: LoginInput) => {
      const response = await api.post<unknown>('/auth/login', input, { anonymous: true });
      return authSuccessSchema.parse(response);
    },
    onSuccess: onAuthSuccess,
  });
}

export function useRegister(): UseMutationResult<AuthSuccess, Error, RegisterInput> {
  const onAuthSuccess = useAuthSuccessHandler();

  return useMutation({
    mutationFn: async ({ confirmPassword: _confirmPassword, ...input }: RegisterInput) => {
      // Nigeria is stated on the screen, not selected (ADR-0019) — the same terms
      // as the phone path below.
      const response = await api.post<unknown>(
        '/auth/register',
        { ...input, country: 'NG' },
        { anonymous: true },
      );
      return authSuccessSchema.parse(response);
    },
    onSuccess: onAuthSuccess,
  });
}

/**
 * Clearing client state alone would leave the refresh cookie valid, so the next
 * page load would silently sign the user back in. The API must revoke it.
 */
export function useLogout(): UseMutationResult<void, Error, void> {
  const clearSession = useSessionStore((state) => state.clearSession);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await api.post<unknown>('/auth/logout');
    },
    onSettled: () => {
      clearSession();
      queryClient.clear();
    },
  });
}

/**
 * Step one: ask the API to send a code (ADR-0024).
 *
 * Answers identically whether or not an account holds the number, so nothing here
 * branches on the response and nothing should be shown that implies recognition.
 */
export function usePhoneStart(): UseMutationResult<PhoneStartResponse, Error, PhoneStartInput> {
  return useMutation({
    mutationFn: async (input: PhoneStartInput) => {
      const response = await api.post<unknown>('/auth/phone/start', input, { anonymous: true });
      return phoneStartResponseSchema.parse(response);
    },
  });
}

/**
 * Step two: redeem the code. A known number signs in here and the caller never
 * reaches step three; an unknown one comes back with a completion token.
 */
export function usePhoneVerify(): UseMutationResult<
  PhoneVerifyResponse,
  Error,
  { verificationId: string; phoneNumber: string; code: string }
> {
  const onAuthSuccess = useAuthSuccessHandler();

  return useMutation({
    mutationFn: async (input) => {
      const response = await api.post<unknown>('/auth/phone/verify', input, { anonymous: true });
      return phoneVerifyResponseSchema.parse(response);
    },
    onSuccess: (result) => {
      if (result.status === 'signed_in') {
        onAuthSuccess(result.session);
      }
    },
  });
}

/**
 * Step three: create the account from a verified number.
 *
 * The number is not sent. It travels inside the completion token, which is the only
 * thing proving a code was redeemed for it; a body-supplied number alongside the
 * token would let anyone register against someone else's.
 */
export function usePhoneRegister(): UseMutationResult<
  AuthSuccess,
  Error,
  PhoneRegisterInput & { phoneToken: string }
> {
  const onAuthSuccess = useAuthSuccessHandler();

  return useMutation({
    mutationFn: async ({ businessName, phoneToken }) => {
      const response = await api.post<unknown>(
        '/auth/register',
        { businessName, country: 'NG', phoneToken },
        { anonymous: true },
      );
      return authSuccessSchema.parse(response);
    },
    onSuccess: onAuthSuccess,
  });
}
